import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthStore } from "../../state/authStore";
import { childrenApi, Video } from "../../api/children";
import {
  adminContentApi,
  contentApi,
  publicContentApi,
  GeneratedVideo,
} from "../../api/content";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { WebVideoCard, WebVideo } from "../components/WebVideoCard";
import { WebSpotlightCard } from "../components/WebSpotlightCard";
import { WebSeriesCard } from "../components/WebSeriesCard";
import { publicSeriesApi, SeriesListItem } from "../../api/series";
import { ChildWithProgress } from "../WebShell";
import { prettyTitle } from "../prettyTitle";

const CATEGORIES = [
  "All",
  "Numbers",
  "Letters",
  "Colors",
  "Shapes",
  "Social Skills",
  "Language",
  "Bedtime",
  "Creativity",
];

const toWebVideo = (v: Video | any): WebVideo => ({
  id: v.id,
  title: prettyTitle(v.title) || v.title,
  category: (v.categories?.[0] ?? "Video") as string,
  duration: v.duration,
  isPremium: v.isPremium,
  ageMin: v.ageMin,
  ageMax: v.ageMax,
  thumbUrl: v.thumbUrl ?? null,
});


type Props = {
  child: ChildWithProgress | null;
  // All kids on the account — used to map publication childIds → names
  // when rendering "Published for Leo, Mila" badges in the My-videos row.
  kids?: ChildWithProgress[];
  onPlay: (v: WebVideo) => void;
  // Open a TV show's info page (seasons + episodes).
  onOpenSeries?: (seriesId: string) => void;
  onGoStudio?: () => void;
  onGoControls: () => void;
  onGoCowatch: () => void;
  liveCowatch: boolean;
  // Guest (logged-out) browsing: hide account-only rows/CTAs and surface a
  // sign-in prompt instead. The catalog still loads (demo/free videos).
  isGuest?: boolean;
  onSignIn?: () => void;
  // Guest taps "Start free trial" — routes through signup, then the trial
  // auto-activates once they're authed.
  onStartFreeTrial?: () => void;
};

export function WebHome({
  child,
  kids,
  onPlay,
  onOpenSeries,
  onGoStudio,
  onGoControls,
  onGoCowatch,
  liveCowatch,
  isGuest,
  onSignIn,
  onStartFreeTrial,
}: Props) {
  const { accessToken } = useAuthStore();
  const [recs, setRecs] = useState<WebVideo[]>([]);
  const [cont, setCont] = useState<WebVideo[]>([]);
  const [catalog, setCatalog] = useState<WebVideo[]>([]);
  // Distinguishes "still loading" from "genuinely empty" so the catalog area
  // doesn't flash the "Nothing here yet" empty state before the fetch lands.
  const [catalogLoading, setCatalogLoading] = useState(true);
  // The parent's own generated videos. We keep the raw `GeneratedVideo`
  // shape here (not WebVideo) so we still have `publications` for the
  // "Published for…" badge and `generationStatus` to filter for `done`.
  const [myVideos, setMyVideos] = useState<GeneratedVideo[]>([]);
  const [series, setSeries] = useState<SeriesListItem[]>([]);
  const [cat, setCat] = useState<string>("All");

  // TV shows load for everyone (public catalog), same as videos.
  useEffect(() => {
    let cancelled = false;
    publicSeriesApi
      .list()
      .then((r) => {
        if (!cancelled) setSeries(r.series);
      })
      .catch(() => {
        /* renders no rail if it errors */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Catalog loads for EVERYONE, including logged-out guests. Signed-in users
  // get their personalized catalog; guests get the public "free library"
  // (/v1/public/videos — real, owner-opted-in content, no account needed).
  //
  // This screen is child-contextual (it greets the child and shows their
  // progress), so the grid honours that child's "Allowed categories". It used
  // to call `list({})` unscoped, which let a restricted child browse and play
  // the entire catalog from here regardless of the parent's setting. Guests
  // have no child and no restriction, so the public list is unfiltered.
  const allowedCategories = child?.controls?.categories ?? [];
  const allowedKey = allowedCategories.join(",");
  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    (async () => {
      try {
        const r = accessToken
          ? await contentApi.list(
              allowedKey ? { categories: allowedKey } : {},
              accessToken,
            )
          : await publicContentApi.list();
        if (!cancelled) setCatalog(r.videos.map(toWebVideo));
      } catch {
        /* signed-in path falls back to demo internally; public list just
           renders empty if it errors */
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `allowedKey` (not the array) so a re-render with an equal list doesn't refetch.
  }, [accessToken, allowedKey]);

  // Account-only rows (recommendations, continue-watching, the parent's own
  // generated videos) need a token + an active child.
  useEffect(() => {
    if (!accessToken || !child) return;
    let cancelled = false;
    (async () => {
      const [recsR, contR, myR] = await Promise.allSettled([
        childrenApi.getRecommendations(child.id, accessToken),
        childrenApi.getContinueWatching(child.id, accessToken),
        adminContentApi.listMyGenerations(accessToken),
      ]);
      if (cancelled) return;
      if (recsR.status === "fulfilled") setRecs(recsR.value.map(toWebVideo));
      if (contR.status === "fulfilled") setCont(contR.value.map(toWebVideo));
      // Show only finished generations in the My-videos row — in-flight rows
      // live in Studio's right-pane gen-card list. Failed ones aren't useful
      // here either; the parent re-tries from Studio.
      // Only surface finished generations that actually have a poster — a
      // video with no poster can't be published, so showing it here just
      // renders a broken card. Those stay in Studio until a poster is added.
      if (myR.status === "fulfilled")
        setMyVideos(
          myR.value.filter(
            (v) => v.generationStatus === "done" && !!v.thumbUrl,
          ),
        );
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, child]);

  // Map childId → name for the publication badge. Memoized so we don't
  // rebuild the Map on every card render.
  const kidNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const k of kids ?? []) m.set(k.id, k.name);
    return m;
  }, [kids]);

  const featured = recs[0] ?? catalog[0] ?? null;
  // A small curated strip for the wide spotlight cards. Prefer recommended
  // titles, fall back to the catalog; cap at 5 so the row stays a highlight.
  const spotlight = useMemo(
    () => (recs.length > 0 ? recs : catalog).slice(0, 5),
    [recs, catalog],
  );
  const filtered =
    cat === "All"
      ? catalog
      : catalog.filter((v) => v.category.toLowerCase() === cat.toLowerCase());

  const childName = child?.name ?? "your child";

  return (
    <div className="fadeup">
      {/* Hero */}
      <div className="w-hero">
        <div style={{ padding: "40px 48px", position: "relative", zIndex: 1 }}>
          {!isGuest && (
            <span className="w-hero-tag">
              <Icon name="sparkle" size={12} /> {`Today's pick for ${childName}`}
            </span>
          )}
          <h1
            style={{
              overflowWrap: "anywhere",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {isGuest
              ? "Cartoons that teach"
              : featured
                ? prettyTitle(featured.title)
                : "Welcome to Kidly"}
          </h1>
          <p>
            Bite-sized cartoons that pause to ask one good question. Safe for
            ages 2–7
            {isGuest
              ? " — start watching free, sign up to save progress and set up profiles."
              : ", and you stay in control from any screen."}
          </p>
          <div className="btn-row">
            {featured && (
              <button className="btn" onClick={() => onPlay(featured)}>
                <Icon name="play" size={16} />{" "}
                {isGuest ? "Watch now" : "Play on TV"}
              </button>
            )}
            {/* Guests get a free-trial CTA next to Watch now — it routes
                through signup and auto-activates the no-card trial. */}
            {isGuest && onStartFreeTrial && (
              <button className="btn btn-ghost" onClick={onStartFreeTrial}>
                <Icon name="sparkle" size={16} /> Start 30-day free trial
              </button>
            )}
            {!isGuest && (
              <button className="btn btn-ghost" onClick={onGoControls}>
                <Icon name="controls" size={16} /> Open controls
              </button>
            )}
          </div>
        </div>
        <div className="w-hero-art">
          <div className="bob">
            <WebMascot mood="happy" color="#FFFFFF" size={300} />
          </div>
        </div>
      </div>

      {/* Live banner */}
      {liveCowatch && (
        <div className="w-section">
          <button
            onClick={onGoCowatch}
            style={{
              width: "100%",
              border: 0,
              borderRadius: 18,
              padding: "14px 22px",
              background: "linear-gradient(90deg, #FF8A4C 0%, #FFA068 100%)",
              color: "#fff",
              fontWeight: 800,
              fontSize: 15,
              display: "flex",
              alignItems: "center",
              gap: 14,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(180,90,36,0.18)",
              fontFamily: "inherit",
            }}
          >
            <span style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 18 }}>
              <span style={{ display: "block", width: 3, height: 11, background: "#fff", borderRadius: 2 }} />
              <span style={{ display: "block", width: 3, height: 18, background: "#fff", borderRadius: 2 }} />
              <span style={{ display: "block", width: 3, height: 8, background: "#fff", borderRadius: 2 }} />
              <span style={{ display: "block", width: 3, height: 14, background: "#fff", borderRadius: 2 }} />
            </span>
            {childName} is watching on the TV — tap to co-watch & control
          </button>
        </div>
      )}

      {/* Featured — wide cinematic spotlight cards (distinct card style). */}
      {spotlight.length > 0 && (
        <div className="w-section">
          <div className="w-section-h">
            <h2>Featured this week</h2>
          </div>
          <Slider count={spotlight.length}>
            {spotlight.map((v, i) => (
              <div
                key={v.id}
                style={{
                  width: 460,
                  maxWidth: "82vw",
                  flex: "0 0 auto",
                  marginRight: 18,
                }}
              >
                <WebSpotlightCard
                  video={v}
                  onPlay={onPlay}
                  eyebrow={i === 0 ? "Editor's pick" : "Featured"}
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {/* TV Shows — multi-episode series; tap a card to open its info page. */}
      {series.length > 0 && (
        <div className="w-section">
          <div className="w-section-h">
            <h2>TV Shows</h2>
          </div>
          <Slider count={series.length}>
            {series.map((s) => (
              <div
                key={s.id}
                style={{
                  width: 280,
                  maxWidth: "70vw",
                  flex: "0 0 auto",
                  marginRight: 16,
                }}
              >
                <WebSeriesCard
                  series={s}
                  onOpen={() => onOpenSeries?.(s.id)}
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {/* Continue */}
      {cont.length > 0 && (
        <div className="w-section">
          <div className="w-section-h">
            <h2>Pick up where {childName} left off</h2>
          </div>
          <Slider count={cont.length}>
            {cont.map((v, i) => (
              <div
                key={v.id}
                style={{
                  width: 280,
                  flex: "0 0 280px",
                  marginRight: 18,
                }}
              >
                <WebVideoCard
                  video={v}
                  onPlay={onPlay}
                  progress={[0.62, 0.34, 0.08, 0.91][i] ?? 0.5}
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {/* My videos — parent's own AI-generated cartoons. Hidden when the
          parent hasn't created any yet so the empty state doesn't add
          visual clutter on a fresh account. */}
      {myVideos.length > 0 && (
        <div className="w-section">
          <div className="w-section-h">
            <h2>My videos</h2>
          </div>
          <Slider count={myVideos.length}>
            {myVideos.map((v) => {
              const pubNames = (v.publications ?? [])
                .map((p) => kidNameById.get(p.childId))
                .filter((n): n is string => Boolean(n));
              const badge =
                pubNames.length > 0
                  ? `For ${pubNames.join(", ")}`
                  : undefined;
              return (
                <div
                  key={v.id}
                  style={{
                    width: 280,
                    flex: "0 0 280px",
                    marginRight: 18,
                  }}
                >
                  <WebVideoCard
                    video={toWebVideo(v)}
                    onPlay={onPlay}
                    badge={badge}
                  />
                </div>
              );
            })}
          </Slider>
        </div>
      )}

      {/* Catalog */}
      <div className="w-section">
        <div className="w-section-h">
          <h2>Browse the catalog</h2>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              className={"w-catchip " + (c === cat ? "active" : "")}
              onClick={() => setCat(c)}
            >
              {c}
            </button>
          ))}
        </div>
        {catalogLoading && filtered.length === 0 ? (
          // Skeleton row while the catalog loads — avoids flashing the
          // "Nothing here yet" empty state before the fetch lands.
          <div style={{ display: "flex", gap: 18, overflow: "hidden" }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  width: 280,
                  height: 232,
                  flex: "0 0 280px",
                  borderRadius: 18,
                  background: "var(--bg-warm)",
                  opacity: 0.6,
                }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="w-empty">
            <WebMascot mood="happy" color="#FF8A4C" size={120} />
            <h2>Nothing here yet</h2>
            <p>Add videos in the admin or pick a different category.</p>
          </div>
        ) : (
          <Slider key={cat} count={filtered.length}>
            {filtered.map((v) => (
              <div
                key={v.id}
                style={{
                  width: 280,
                  flex: "0 0 280px",
                  marginRight: 18,
                }}
              >
                <WebVideoCard video={v} onPlay={onPlay} />
              </div>
            ))}
          </Slider>
        )}
      </div>

      {/* For you (recommendations) */}
      {recs.length > 0 && (
        <div className="w-section" style={{ paddingBottom: 48 }}>
          <div className="w-section-h">
            <h2>Picked for {childName}</h2>
          </div>
          <Slider count={recs.length}>
            {recs.map((v) => (
              <div
                key={v.id}
                style={{
                  width: 280,
                  flex: "0 0 280px",
                  marginRight: 18,
                }}
              >
                <WebVideoCard
                  video={v}
                  onPlay={onPlay}
                  badge="For you"
                />
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Slider — transform-based one-row strip with prev/next arrow buttons.
//
// Browser-scroll approaches (overflow-x: auto + flex / grid track) kept
// fighting the card button's intrinsic content-width sizing. Switched
// to a hard layout: a clipping viewport (overflow: hidden) wrapping an
// inner track laid out as inline-block cells with explicit widths, then
// translateX'd by the arrow buttons. No browser-scroll involved, so no
// chance of flex/grid widget arguing with the card.
// ─────────────────────────────────────────────────────────────────────────
function Slider({
  children,
  count,
}: {
  children: React.ReactNode;
  count: number;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Native horizontal scroll instead of a transform-based track, so the
  // rows respond to touch swipes (with momentum on iOS) — phones expect to
  // drag these, not hunt for arrow buttons. The arrows remain on desktop and
  // just call scrollBy. Scrollbar is hidden via the .w-hscroll CSS class.
  const update = useCallback(() => {
    const v = viewportRef.current;
    if (!v) return;
    setCanLeft(v.scrollLeft > 4);
    setCanRight(v.scrollLeft < v.scrollWidth - v.clientWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const v = viewportRef.current;
    if (!v) return;
    v.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      v.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, count]);

  const page = (dir: -1 | 1) => {
    const v = viewportRef.current;
    if (!v) return;
    v.scrollBy({ left: Math.round(v.clientWidth * 0.8) * dir, behavior: "smooth" });
  };

  const hasOverflow = canLeft || canRight;

  return (
    <div style={{ position: "relative", marginBottom: 10 }}>
      {/* Arrows only when the row actually overflows (nothing to scroll
          otherwise). On touch the user just swipes. */}
      {hasOverflow && (
        <div
          style={{
            position: "absolute",
            top: -42,
            right: 0,
            display: "flex",
            gap: 6,
            zIndex: 2,
          }}
        >
          <SliderArrow direction="left" disabled={!canLeft} onClick={() => page(-1)} />
          <SliderArrow direction="right" disabled={!canRight} onClick={() => page(1)} />
        </div>
      )}
      <div
        ref={viewportRef}
        className="w-hscroll"
        style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}
      >
        <div
          style={{
            display: "inline-flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            alignItems: "flex-start",
            width: "max-content",
            // Room for the card hover-lift (translateY) so it doesn't get
            // clipped by the viewport's overflowY: hidden.
            padding: "8px 0",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// Plain chevron arrow — no circle, no shadow. Disabled state just fades
// it out + drops pointer events.
function SliderArrow({
  direction,
  disabled,
  onClick,
}: {
  direction: "left" | "right";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === "left" ? "Previous" : "Next"}
      style={{
        background: "transparent",
        border: 0,
        padding: 6,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.25 : 1,
        pointerEvents: disabled ? "none" : "auto",
        color: "var(--ink)",
        font: "inherit",
        display: "grid",
        placeItems: "center",
        transition: "opacity 160ms ease",
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d={direction === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"}
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export default WebHome;
